import { useDebounce } from "ahooks";
import { Input, Modal, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { MODULE } from "../../../../../../constants/vosConstants";
import { HrDataSearchItem } from "../../../../../../services/vos/permission-lookup/hr-data-search.service";
import useHrDataSearch from "../../../../info-technology-process/hooks/use-hr-data-search";

interface SearchEmployeeModalProps {
  module?: string;
  unit?: number | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: (employees: HrDataSearchItem[]) => void;
  existingEmployees?: HrDataSearchItem[];
}

const SearchEmployeeModal = (props: SearchEmployeeModalProps) => {
  const { module = MODULE.USER, unit, open, setOpen, onConfirm } = props;

  const [search, setSearch] = useState("");

  const searchDebounce = useDebounce(search, {
    wait: 500,
  });

  const { data, isLoading, isFetching } = useHrDataSearch({
    module,
    keySearch: searchDebounce || "",
    depID: unit ?? undefined,
  });

  const loading = (isLoading || isFetching) && Boolean(searchDebounce);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<HrDataSearchItem[]>([]);

  const employees: HrDataSearchItem[] = useMemo(
    () => (searchDebounce ? data?.Data ?? [] : []),
    [data?.Data, searchDebounce],
  );

  // Đồng bộ danh sách nhân viên đã chọn với state `employees` ở PermissionLookupForm
  // useEffect(() => {
  //   const initialEmployees = existingEmployees ?? [];
  //   setSelectedEmployees(initialEmployees);
  //   setSelectedRowKeys(initialEmployees.map((e) => e.HR_ID));
  // }, [existingEmployees, open]);

  const columns: ColumnsType<HrDataSearchItem> = useMemo(() => {
    const buildFilters = (field: keyof HrDataSearchItem) => {
      const values = Array.from(
        new Set(
          employees
            .map((e) => e[field])
            .filter((v): v is string | number => v !== null && v !== undefined && `${v}`.trim() !== ""),
        ),
      );
      return values.map((v) => ({ text: `${v}`, value: `${v}` }));
    };

    const filterableString = (field: keyof HrDataSearchItem) => ({
      filters: buildFilters(field),
      filterSearch: true,
      onFilter: (value: string | number | boolean, record: HrDataSearchItem) =>
        `${record[field] || ""}`.toLowerCase().startsWith(String(value).toLowerCase()),
    });

    return [
      {
        title: "TT",
        key: "stt",
        align: "center",
        fixed: true,
        render: (_: any, __: HrDataSearchItem, index: number) => index + 1,
      },
      {
        title: "Mã nhân viên",
        dataIndex: "UserCode",
        key: "UserCode",
        fixed: true,
        ...filterableString("UserCode"),
      },
      {
        title: "Tên nhân viên",
        dataIndex: "UserName",
        key: "UserName",
        fixed: true,
        ...filterableString("UserName"),
      },
      {
        title: "Chức danh",
        key: "Name_Job_Title",
        render: (_: any, record: HrDataSearchItem) =>
          [record.Code_Job_Title, record.Name_Job_Title].filter(Boolean).join(" - "),
        filters: buildFilters("Name_Job_Title"),
        filterSearch: true,
        onFilter: (value: string | number | boolean, record: HrDataSearchItem) =>
          `${record.Name_Job_Title || ""}`.toLowerCase().startsWith(String(value).toLowerCase()),
      },
      {
        title: "Đơn vị (Cấp 1)",
        dataIndex: "Level_1_Name",
        key: "Level_1_Name",
        ...filterableString("Level_1_Name"),
      },
      {
        title: "Đơn vị (Cấp 2)",
        dataIndex: "Level_2_Name",
        key: "Level_2_Name",
        ...filterableString("Level_2_Name"),
      },
      {
        title: "Đơn vị (Cấp 3)",
        dataIndex: "Level_3_Name",
        key: "Level_3_Name",
        ...filterableString("Level_3_Name"),
      },
    ];
  }, [employees]);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  return (
    <Modal
      title="Tìm kiếm nhân viên"
      width="90%"
      open={open}
      destroyOnClose
      onCancel={() => setOpen(false)}
      onOk={() => {
        onConfirm(selectedEmployees);
        setOpen(false);
        setSearch("");
        setSelectedEmployees([]);
        setSelectedRowKeys([]);
      }}
      okText="Thêm"
      cancelText="Huỷ"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm font-medium">Đã chọn {selectedEmployees.length} nhân viên</span>
          </div>
          <div className="flex justify-end flex-1">
            <Input
              allowClear
              placeholder="Tên/mã nhân viên"
              style={{
                maxWidth: "500px",
              }}
              onChange={handleChangeSearch}
            />
          </div>
        </div>

        <Table<HrDataSearchItem>
          bordered
          size="middle"
          columns={columns}
          dataSource={employees}
          rowKey="HR_ID"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Tổng ${total} nhân viên`,
          }}
          scroll={{ x: "max-content" }}
          rowSelection={{
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange: (keys, rows) => {
              setSelectedRowKeys(keys);
              setSelectedEmployees((prev) => {
                const map = new Map(prev.map((emp) => [emp.HR_ID, emp]));
                rows.forEach((emp) => map.set(emp.HR_ID, emp));
                return Array.from(map.values()).filter((emp) => keys.includes(emp.HR_ID));
              });
            },
          }}
        />
      </div>
    </Modal>
  );
};

export default SearchEmployeeModal;
